import {
  Add as AddIcon,
  ChatBubble as ChatBubbleIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from '../icons';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Box,
  Divider,
  FormControl,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  TextField,
  IconButton,
  Chip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IMAGE_PLACEHOLDER } from '../../config';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useCart } from '../../contexts/CartContext';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';
import OptimizedImage from '../common/OptimizedImage';

const MenuItemDetailsDialog = ({
  open,
  onClose,
  item,
  onAddToCart,
  editMode = false,
  editIndex = -1,
  onUpdate,
  initialQuantity,
  initialOptions,
  initialNotes,
  orderingDisabled = false,
  orderingDisabledReason = '',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);

  const { formatCurrency } = useCurrencyFormatter();
  const { addNotification } = useNotification();
  const { items: cartItems, updateItemQuantity } = useCart();
  const { primaryColor } = usePublicTheme();

  const getLockedGroupIds = () => {
    if (!item) return new Set();
    const groups = item.option_groups || [];
    const locked = new Set();
    for (const group of groups) {
      if (group.requirement_type === 'PER_TABLE' && group.is_required) {
        locked.add(group.id);
      }
    }
    return locked;
  };

  const lockedGroupIds = getLockedGroupIds();

  useEffect(() => {
    if (item) {
      if (editMode) {
        setQuantity(initialQuantity ?? item.quantity ?? 1);
        setSelectedOptions(initialOptions ?? item.options ?? []);
        setNotes(initialNotes ?? item.notes ?? '');
        return;
      }

      const autoSelected = [];
      for (const group of (item.option_groups || [])) {
        if (group.requirement_type === 'PER_TABLE' && group.is_required && group.items?.length > 0) {
          autoSelected.push({ ...group.items[0], _groupId: group.id });
        }
      }

      if (!item.has_options) {
        const existingCartItem = cartItems.find(
          ci => ci.id === item.id && (!ci.options || ci.options.length === 0)
        );

        if (existingCartItem) {
          setQuantity(existingCartItem.quantity);
          setSelectedOptions(existingCartItem.options || []);
          setNotes(existingCartItem.notes || '');
        } else {
          setQuantity(1);
          setSelectedOptions(autoSelected);
          setNotes('');
        }
      } else {
        setQuantity(1);
        setSelectedOptions(autoSelected);
        setNotes('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, editMode, initialQuantity, initialOptions, initialNotes]);

  // Calculate total price based on selected options
  useEffect(() => {
    if (!item) return;

    let price = parseFloat(item.price) || 0;

    selectedOptions.forEach(option => {
      price += parseFloat(option.price_addition) || 0;
    });

    setTotalPrice(price * quantity);
  }, [item, selectedOptions, quantity]);

  if (!item) return null;

  const handleQuantityChange = (change) => {
    const newQuantity = Math.max(1, quantity + change);
    setQuantity(newQuantity);
  };

  const handleOptionChange = (option, groupId, isRadio = false) => {
    if (lockedGroupIds.has(groupId)) return;
    const taggedOption = { ...option, _groupId: groupId };

    if (isRadio) {
      const filteredOptions = selectedOptions.filter(
        selectedOption => selectedOption._groupId !== groupId
      );
      setSelectedOptions([...filteredOptions, taggedOption]);
    } else {
      const optionIndex = selectedOptions.findIndex(
        selectedOption => selectedOption.id === option.id
      );

      if (optionIndex === -1) {
        setSelectedOptions([...selectedOptions, taggedOption]);
      } else {
        const newOptions = [...selectedOptions];
        newOptions.splice(optionIndex, 1);
        setSelectedOptions(newOptions);
      }
    }
  };

  const isOptionSelected = (option) => {
    return selectedOptions.some(selectedOption => selectedOption.id === option.id);
  };

  const handleAddToCart = () => {
    if (editMode && onUpdate) {
      onUpdate(editIndex, {
        quantity,
        options: selectedOptions,
        notes,
      });
      addNotification(`Updated ${item.name}`, 'primary');
      onClose();
      return;
    }

    // For items without options, check if we're updating an existing cart item
    if (!item.has_options) {
      const existingCartItemIndex = cartItems.findIndex(
        cartItem => cartItem.id === item.id &&
        (!cartItem.options || cartItem.options.length === 0)
      );

      if (existingCartItemIndex !== -1) {
        updateItemQuantity(existingCartItemIndex, quantity);
        addNotification(`Updated ${item.name} quantity to ${quantity}`, 'primary');
        onClose();
        return;
      }
    }

    const itemWithDetails = {
      ...item,
      quantity,
      options: selectedOptions,
      notes: notes,
      _optionGroupsMeta: optionGroups.map(g => ({
        id: g.id,
        name: g.name,
        requirement_type: g.requirement_type,
        items: g.items,
      })),
    };

    onAddToCart(itemWithDetails);
    addNotification(`Added ${quantity}x ${item.name} to cart`, 'primary');
    onClose();
  };

  const optionGroups = item.option_groups || [];

  const missingRequiredGroups = optionGroups
    .filter(group => group.is_required && !lockedGroupIds.has(group.id))
    .filter(group => !selectedOptions.some(opt => opt._groupId === group.id));

  const canAddToCart = item.is_available !== false && missingRequiredGroups.length === 0 && !orderingDisabled;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : { xs: '16px', sm: '24px' },
          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3)',
          margin: isMobile ? 0 : { xs: 1, sm: 2 },
          maxHeight: isMobile ? '100vh' : { xs: '95vh', sm: '90vh' },
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1.5, sm: 2 }, pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: '#1f2937',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.name}
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
            size={isMobile ? "small" : "medium"}
            sx={{
              borderRadius: 3,
              transition: 'all 0.3s ease',
              flexShrink: 0,
              '&:hover': {
                transform: 'rotate(90deg) scale(1.1)',
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{
              borderRadius: { xs: 2, sm: 4 },
              maxHeight: { xs: 250, sm: 380 },
              width: '100%',
              overflow: 'hidden',
              boxShadow: '0 12px 35px rgba(0, 0, 0, 0.18)',
            }}>
              <OptimizedImage
                src={item.image || IMAGE_PLACEHOLDER}
                alt={item.name}
                fallbackSrc={IMAGE_PLACEHOLDER}
                priority
                sx={{
                  maxHeight: { xs: 250, sm: 380 },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={2}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#1f2937',
                  fontSize: { xs: '1.25rem', sm: '2rem' },
                }}
              >
                {item.name}
              </Typography>
              {item.category && (
                <Chip
                  label={item.category.name}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    mb: 2,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: 3,
                    height: { xs: 28, sm: 36 },
                    border: 'none',
                    boxShadow: `0 6px 16px ${hexToRgba(primaryColor, 0.35)}`,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '& .MuiChip-label': {
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 700,
                    }
                  }}
                />
              )}
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1.05rem' },
                  lineHeight: 1.7,
                  color: '#4b5563'
                }}
              >
                {item.description}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#1f2937',
                  fontSize: { xs: '1.25rem', sm: '1.75rem' },
                }}
              >
                {formatCurrency(item.price)}
              </Typography>
            </Box>
          </Grid>

          {/* Options */}
          {optionGroups.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  mb: 3,
                  color: '#1f2937',
                }}
              >
                Options
              </Typography>

              {optionGroups.map((group) => (
                <Box key={group.id} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: '#1f2937',
                      }}
                    >
                      {group.name}
                    </Typography>
                    {group.is_required && lockedGroupIds.has(group.id) && (
                      <Chip
                        label="Included"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          background: `${hexToRgba(primaryColor, 0.1)}`,
                          color: primaryColor,
                        }}
                      />
                    )}
                    {group.is_required && !lockedGroupIds.has(group.id) && (
                      <Chip
                        label="Required"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          background: missingRequiredGroups.some(g => g.id === group.id)
                            ? 'rgba(239, 68, 68, 0.1)' : `${hexToRgba(primaryColor, 0.1)}`,
                          color: missingRequiredGroups.some(g => g.id === group.id)
                            ? '#ef4444' : primaryColor,
                        }}
                      />
                    )}
                    {!group.is_required && group.requirement_type === 'PER_TABLE' && (
                      <Chip
                        label="Required for takeaway"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          background: 'rgba(245, 158, 11, 0.1)',
                          color: '#f59e0b',
                        }}
                      />
                    )}
                  </Box>
                  <FormControl component="fieldset" fullWidth>
                    {(() => {
                      const isLocked = lockedGroupIds.has(group.id);
                      const optionItems = group.items || [];

                      if (group.selection_type === 'SINGLE') {
                        return (
                          <RadioGroup>
                            {optionItems.map((option) => (
                              <Box
                                key={option.id}
                                onClick={() => handleOptionChange(option, group.id, true)}
                                sx={{
                                  mb: 1.5,
                                  p: 2,
                                  borderRadius: 3,
                                  opacity: isLocked ? 0.7 : 1,
                                  background: isOptionSelected(option)
                                    ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.12)} 0%, ${hexToRgba(primaryColor, 0.08)} 100%)`
                                    : '#ffffff',
                                  border: isOptionSelected(option)
                                    ? `2px solid ${primaryColor}`
                                    : '2px solid rgba(0, 0, 0, 0.08)',
                                  cursor: isLocked ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: isOptionSelected(option)
                                    ? `0 4px 12px ${hexToRgba(primaryColor, 0.2)}`
                                    : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                  ...(!isLocked && {
                                    '&:hover': {
                                      borderColor: primaryColor,
                                      boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.15)}`,
                                      transform: 'translateY(-2px)',
                                      background: isOptionSelected(option)
                                        ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.15)} 0%, ${hexToRgba(primaryColor, 0.1)} 100%)`
                                        : hexToRgba(primaryColor, 0.02),
                                    },
                                  }),
                                }}
                              >
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box display="flex" alignItems="center" gap={1.5}>
                                    <Radio
                                      checked={isOptionSelected(option)}
                                      disabled={isLocked}
                                      sx={{
                                        p: 0,
                                        color: primaryColor,
                                        '&.Mui-checked': { color: primaryColor },
                                        '&.Mui-disabled.Mui-checked': { color: primaryColor, opacity: 0.6 },
                                      }}
                                    />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: isLocked ? '#6b7280' : 'inherit' }}>
                                      {option.name}
                                    </Typography>
                                  </Box>
                                  {parseFloat(option.price_addition) > 0 && (
                                    <Typography sx={{ fontWeight: 800, color: isLocked ? '#9ca3af' : '#1f2937', fontSize: '0.95rem' }}>
                                      +{formatCurrency(option.price_addition)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </RadioGroup>
                        );
                      }
                      return (
                        <FormGroup>
                          {optionItems.map((option) => (
                            <Box
                              key={option.id}
                              onClick={() => handleOptionChange(option, group.id)}
                              sx={{
                                mb: 1.5,
                                p: 2,
                                borderRadius: 3,
                                opacity: isLocked ? 0.7 : 1,
                                background: isOptionSelected(option)
                                  ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.12)} 0%, ${hexToRgba(primaryColor, 0.08)} 100%)`
                                  : '#ffffff',
                                border: isOptionSelected(option)
                                  ? `2px solid ${primaryColor}`
                                  : '2px solid rgba(0, 0, 0, 0.08)',
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: isOptionSelected(option)
                                  ? `0 4px 12px ${hexToRgba(primaryColor, 0.2)}`
                                  : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                ...(!isLocked && {
                                  '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.15)}`,
                                    transform: 'translateY(-2px)',
                                    background: isOptionSelected(option)
                                      ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.15)} 0%, ${hexToRgba(primaryColor, 0.1)} 100%)`
                                      : hexToRgba(primaryColor, 0.02),
                                  },
                                }),
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Checkbox
                                    checked={isOptionSelected(option)}
                                    disabled={isLocked}
                                    sx={{
                                      p: 0,
                                      color: primaryColor,
                                      '&.Mui-checked': { color: primaryColor },
                                      '&.Mui-disabled.Mui-checked': { color: primaryColor, opacity: 0.6 },
                                    }}
                                  />
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: isLocked ? '#6b7280' : 'inherit' }}>
                                    {option.name}
                                  </Typography>
                                </Box>
                                {parseFloat(option.price_addition) > 0 && (
                                  <Typography sx={{ fontWeight: 800, color: isLocked ? '#9ca3af' : '#1f2937', fontSize: '0.95rem' }}>
                                    +{formatCurrency(option.price_addition)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </FormGroup>
                      );
                    })()}
                  </FormControl>
                </Box>
              ))}
            </Grid>
          )}

          {/* Special Instructions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                fontSize: '1.05rem',
                mb: 2,
                color: '#1f2937',
              }}
            >
              <ChatBubbleIcon sx={{ fontSize: 'inherit', verticalAlign: '-0.15em', mr: 0.75 }} />
              Special Instructions
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Any special requests or allergies?"
              variant="outlined"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  background: 'rgba(249, 250, 251, 0.8)',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: hexToRgba(primaryColor, 0.2),
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: hexToRgba(primaryColor, 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: primaryColor,
                    borderWidth: '2px',
                  },
                  '&:hover': {
                    boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.1)}`,
                  },
                  '&.Mui-focused': {
                    background: '#ffffff',
                    boxShadow: `0 6px 20px ${hexToRgba(primaryColor, 0.15)}`,
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        justifyContent: orderingDisabled ? 'center' : 'space-between',
        p: { xs: 2, sm: 3.5 },
        gap: { xs: 1.5, sm: 2.5 },
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
      }}>
        {orderingDisabled ? (
          <Box sx={{
            width: '100%',
            py: 1,
            px: 2,
            borderRadius: { xs: 3, sm: 4 },
            background: orderingDisabledReason === 'outside_hours'
              ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
              : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            border: orderingDisabledReason === 'outside_hours'
              ? '1px solid #c4b5fd'
              : '1px solid #fca5a5',
            textAlign: 'center',
          }}>
            <Typography sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              color: orderingDisabledReason === 'outside_hours' ? '#5b21b6' : '#991b1b',
            }}>
              {orderingDisabledReason === 'outside_hours'
                ? "We're currently outside business hours."
                : 'We are currently closed. Please check back later.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" justifyContent="center" sx={{
              gap: 1.5,
              background: 'rgba(0, 0, 0, 0.04)',
              borderRadius: { xs: 3, sm: 4 },
              p: { xs: 1, sm: 1.5 },
              width: { xs: '100%', sm: 'auto' },
            }}>
              <IconButton
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                size={isMobile ? "small" : "medium"}
                sx={{
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'scale(1.12)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <RemoveIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  mx: { xs: 2, sm: 2.5 },
                  fontWeight: 800,
                  minWidth: { xs: 35, sm: 45 },
                  textAlign: 'center',
                  color: '#1f2937',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                }}
              >
                {quantity}
              </Typography>
              <IconButton
                onClick={() => handleQuantityChange(1)}
                size={isMobile ? "small" : "medium"}
                sx={{
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'scale(1.12)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddToCart}
              disabled={!canAddToCart}
              startIcon={editMode ? undefined : <AddIcon />}
              fullWidth={isMobile}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: { xs: 3, sm: 4 },
                px: { xs: 3, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.35)',
                }
              }}
            >
              {editMode
                ? `Update - ${formatCurrency(totalPrice)}`
                : `Add to Cart - ${formatCurrency(totalPrice)}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MenuItemDetailsDialog;
